import { useEffect, useState } from "react";
import api from "../lib/api";
import { X, Trophy } from "lucide-react";

/**
 * TopGiftersWidget — shows up to 3 small overlapping avatars of today's top
 * gifters for a given host. Tapping opens a sheet with Today / Last 7 days /
 * Overall tabs. Overall = all-time (support fix #5 renamed "Last 90").
 */
export default function TopGiftersWidget({ hostUserId }) {
  const [today, setToday] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!hostUserId) return;
    api.get(`/users/${hostUserId}/top-gifters`, { params: { period: "today", limit: 3 } })
      .then(r => setToday(r.data || []))
      .catch(() => {});
  }, [hostUserId]);

  if (!hostUserId) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="top-gifters-widget"
        className="flex items-center -space-x-2 active:scale-95"
        aria-label="View top gifters"
      >
        {today.length === 0 ? (
          <div className="w-7 h-7 rounded-full bg-black/60 border border-[#FF7A00]/50 flex items-center justify-center">
            <Trophy className="w-3.5 h-3.5 text-[#FF7A00]"/>
          </div>
        ) : (
          today.slice(0, 3).map((g, i) => (
            <img
              key={g.user?.id || i}
              src={g.user?.avatar}
              alt=""
              className={`w-7 h-7 rounded-full border-2 ${i === 0 ? "border-[#FFD700]" : i === 1 ? "border-[#C0C0C0]" : "border-[#CD7F32]"}`}
            />
          ))
        )}
      </button>

      {open && <TopGiftersSheet hostUserId={hostUserId} onClose={() => setOpen(false)}/>}
    </>
  );
}

function TopGiftersSheet({ hostUserId, onClose }) {
  const [tab, setTab] = useState("today");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/users/${hostUserId}/top-gifters`, { params: { period: tab, limit: 50 } })
      .then(r => { setRows(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [hostUserId, tab]);

  return (
    <div className="fixed inset-0 z-[80] max-w-md mx-auto" data-testid="top-gifters-sheet">
      <div className="absolute inset-0 bg-black/70" onClick={onClose}/>
      <div className="absolute bottom-0 inset-x-0 bg-[#0B0F19] rounded-t-3xl border-t border-white/10 max-h-[75vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="font-outfit font-bold text-lg flex items-center gap-2"><Trophy className="w-5 h-5 text-[#FFD700]"/>Top Gifters</h3>
          <button onClick={onClose} data-testid="top-gifters-close" aria-label="Close"><X className="w-6 h-6"/></button>
        </div>
        <div className="flex gap-1 p-2 border-b border-white/10">
          {[
            { id: "today", label: "Today" },
            { id: "7d", label: "Last 7 days" },
            { id: "all", label: "Overall" },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`top-gifters-tab-${t.id}`}
              className={`flex-1 py-2 rounded-full text-xs font-bold ${tab === t.id ? "bg-[#FF7A00] text-black" : "bg-white/5 text-white/70"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="overflow-y-auto flex-1 p-3 space-y-2">
          {loading && <div className="text-center py-6 text-white/50 text-sm">Loading…</div>}
          {!loading && rows.length === 0 && <div className="text-center py-6 text-white/50 text-sm">No gifters yet</div>}
          {!loading && rows.map(g => (
            <div key={g.user?.id || g.rank} className="flex items-center gap-3 p-2 rounded-xl bg-white/5">
              <span className="w-6 text-center font-outfit font-extrabold text-[#FF7A00]">{g.rank}</span>
              {g.user?.avatar && <img src={g.user.avatar} className="w-9 h-9 rounded-full border border-white/15"/>}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">@{g.user?.username || "user"}</div>
                <div className="text-[10px] text-white/55">{g.gifts_count} gifts</div>
              </div>
              <span className="text-[#FF7A00] font-extrabold text-sm">{g.total_amount}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
